import React from 'react';

import { isEmpty } from 'lodash';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { ClaimDocumentMetadata } from '~/components/Chat/ChatContextDialog';
import ChatContextDialog from '~/components/Chat/ChatContextDialog/ChatContextDialog';
import ChatMessage from '~/components/Chat/ChatMessage';
import { ChatSkeleton } from '~/components/Chat/ChatSkeleton';
import CliveChatMessage from '~/components/Chat/CliveChatMessage';
import CliveChatThinking from '~/components/Chat/CliveChatThinking';
import { NEW_CHAT_TOKEN } from '~/components/Chat/constants';
import { useClaimChatsContext } from '~/components/Chat/context/ChatContext';
import { usePredefinedPromptsContext } from '~/components/Chat/context/PredefinedPromptsContext';
import { UserAvatar } from '~/components/Chat/UserAvatar';
import ChatInput, { ChatInputAction } from '~/components/ChatInput/ChatInput';
import { useClaimDocuments } from '~/components/Documents';
import { UploadedFilesPanel } from '~/components/Fnol/UploadedFilesPanel';
import { useAuth } from '~/context/AuthContext';
import { FirestoreAgentProgressEvent, useRealTimeUpdatesChat } from '~/context/RealTimeUpdatesChatContext';
import ActionArrowIcon from '~/core/iconComponents/icons/ActionArrowIcon';
import ClipIcon from '~/core/iconComponents/icons/ClipIcon.tsx';
import DocumentIcon from '~/core/iconComponents/icons/DocumentIcon';
import FileUploadModal, { UploadedFile } from '~/core/molecules/FileUpload/FileUploadModal';
import cn from '~/utils/cn';
import { parseDateWithOptionalTimezone } from '~/utils/Dates/DateTimeUtils';
import { post } from '~/utils/request';
import { convertEventsToTimelineEntries } from '~/utils/timeline';

const Chat: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { claimNumber = '', chatId: chatIdFromRoute } = useParams();
  const [value, setValue] = React.useState('');
  const [initialized, setInitialized] = React.useState(false);
  const [isChatPageLoading, setIsChatPageLoading] = React.useState(true);
  const [withTypingEffect, setWithTypingEffect] = React.useState(false);
  const endOfChatRef = React.useRef<HTMLDivElement>(null);
  const [isChatContextDialogOpen, setIsChatContextDialogOpen] = React.useState(false);
  const [selectedContextDocuments, setSelectedContextDocuments] = React.useState<ClaimDocumentMetadata[]>([]);
  const [openFileUpload, setOpenFileUpload] = React.useState(false);
  const [uploadedFiles, setUploadedFiles] = React.useState<UploadedFile[]>([]);

  const { isChatLoading, getChatExchanges, createChatExchange, exchanges, resetExchanges } = useClaimChatsContext();
  const { prompts: predefinedPrompts } = usePredefinedPromptsContext();
  const { user } = useAuth();
  const { documents: claimDocuments, isLoading: isDocumentsLoading } = useClaimDocuments({ claimNumber });
  const { organizedUpdates } = useRealTimeUpdatesChat();

  const isNewChat = React.useMemo(() => chatIdFromRoute === NEW_CHAT_TOKEN, [chatIdFromRoute]);

  const getChatId = (isNewChat: boolean, chatIdFromRoute: string | undefined): string | null => {
    return isNewChat ? null : chatIdFromRoute || null;
  };

  const scrollDown = React.useCallback(() => {
    setTimeout(() => {
      if (endOfChatRef.current) {
        endOfChatRef.current.scrollIntoView({ behavior: 'smooth' });
      }
    }, 500);
  }, [endOfChatRef]);

  const updateUrl = React.useCallback(
    async (newChatId?: string) => {
      if (!claimNumber || !newChatId) return;
      if (chatIdFromRoute !== newChatId) {
        await navigate(`/claims/${claimNumber}/chat/${newChatId}`);
      }
    },
    [chatIdFromRoute, claimNumber, navigate]
  );

  const onSubmitFiles = React.useCallback(
    async (files: UploadedFile[]) => {
      // Update uploaded files state to display in panel
      setUploadedFiles(files);

      // Extract document external IDs for AI processing
      const documentExternalIds = files.map((file) => file.document_external_id).filter(Boolean) as string[];

      // If we have document external IDs, process them with AI
      if (!isEmpty(documentExternalIds) && claimNumber) {
        try {
          const aiProcessingUrl = `/api/v1/claims/${claimNumber}/documents/process-ai`;

          await post({
            url: aiProcessingUrl,
            data: {
              document_external_ids: documentExternalIds,
            },
          });
        } catch (error) {
          console.error('AI processing failed:', error);
          // Continue with the flow even if AI processing fails
        }
      }

      setOpenFileUpload(false);
    },
    [claimNumber]
  );

  const onRemoveUploadedFile = React.useCallback((file: UploadedFile) => {
    setUploadedFiles((prev) => prev.filter((f) => f.name !== file.name));
  }, []);

  const chatInputActions: ChatInputAction[] = React.useMemo(
    () => [
      {
        key: 'choose-claim-documents',
        icon: <ClipIcon className="text-grey-300" />,
        onClick: () => setIsChatContextDialogOpen(true),
        ariaLabel: 'Add Context',
        disabled: !claimNumber || isChatLoading || isDocumentsLoading,
      },
      {
        key: 'upload-claim-documents',
        icon: <ActionArrowIcon className="text-grey-300" />,
        onClick: () => setOpenFileUpload(true),
        ariaLabel: 'Upload File',
        disabled: !claimNumber || isChatLoading,
      },
    ],
    [claimNumber, isChatLoading, isDocumentsLoading]
  );

  const sendChatMessage = React.useCallback(
    async (message?: string) => {
      if (!claimNumber || !message?.trim() || isChatLoading) return;
      setWithTypingEffect(true);
      setValue('');
      scrollDown();

      // Create chat exchange with selected context documents
      // Note: uploadedFiles are already processed with AI and available in the AI system
      // They are kept separate from selectedContextDocuments as per user requirements
      const chatId = await createChatExchange({
        claimNumber,
        userInput: message,
        chatId: getChatId(isNewChat, chatIdFromRoute),
        usedDocuments: selectedContextDocuments.map((doc) => doc.id),
      });

      // Clear uploaded files after successful message sending (FNOL pattern)
      if (chatId) {
        setUploadedFiles([]);
      }

      scrollDown();
      await updateUrl(chatId);
      return chatId;
    },
    [
      claimNumber,
      createChatExchange,
      isNewChat,
      chatIdFromRoute,
      updateUrl,
      scrollDown,
      isChatLoading,
      selectedContextDocuments,
    ]
  );

  const handlePromptSelect = React.useCallback(
    async (promptKey: string) => {
      if (!claimNumber || isChatLoading) return;

      // Find the selected prompt to get its title for the temporary exchange
      const selectedPrompt = predefinedPrompts.find((p) => p.prompt_key === promptKey);
      if (!selectedPrompt) return;

      setWithTypingEffect(true);
      scrollDown();

      // Create chat exchange with prompt_key (user_input will be empty)
      // Pass promptTitle to show in temporary exchange
      const chatId = await createChatExchange({
        claimNumber,
        promptKey,
        promptTitle: selectedPrompt.prompt_title,
        chatId: getChatId(isNewChat, chatIdFromRoute),
        usedDocuments: selectedContextDocuments.map((doc) => doc.id),
      });

      // Clear uploaded files after successful prompt sending
      if (chatId) {
        setUploadedFiles([]);
      }

      scrollDown();
      await updateUrl(chatId);
      return chatId;
    },
    [
      claimNumber,
      createChatExchange,
      isNewChat,
      chatIdFromRoute,
      updateUrl,
      scrollDown,
      isChatLoading,
      selectedContextDocuments,
      predefinedPrompts,
    ]
  );

  const loadChat = React.useCallback(
    async (chatId?: string) => {
      if (!claimNumber) return;

      setWithTypingEffect(false);
      await getChatExchanges(claimNumber, chatId || chatIdFromRoute);
    },
    [claimNumber, getChatExchanges, chatIdFromRoute]
  );

  const initializeChat = React.useCallback(async () => {
    if (!claimNumber || initialized) {
      return;
    }

    setInitialized(true);
    resetExchanges();

    if (isNewChat) {
      // For new chats without initial message, hide loading immediately
      if (!location?.state?.userMessage) {
        setIsChatPageLoading(false);
      }
      // If there's an initial message, keep skeleton visible during chat creation
      // It will be hidden by the useEffect watching exchanges

      // Send initial message if provided
      if (location?.state?.userMessage) {
        await sendChatMessage(location?.state?.userMessage);
      }
    } else {
      // Load chat data for existing chats only
      await loadChat();
      setIsChatPageLoading(false);
    }

    scrollDown();
  }, [
    claimNumber,
    initialized,
    isNewChat,
    loadChat,
    resetExchanges,
    sendChatMessage,
    scrollDown,
    location?.state?.userMessage,
  ]);

  React.useEffect(() => {
    if (!initialized) {
      initializeChat();
    }
  }, [initialized, initializeChat]);

  // Hide skeleton once we have exchanges (chat creation complete and temp exchange added)
  React.useEffect(() => {
    if (exchanges.length > 0 && isChatPageLoading) {
      setIsChatPageLoading(false);
    }
  }, [exchanges.length, isChatPageLoading]);

  const isLastExchange = (index: number) => {
    return exchanges.length - 1 === index;
  };

  const getExchangeDuration = (events: FirestoreAgentProgressEvent[]): number | null => {
    if (events.length === 0) return null;

    const sortedEvents = [...events].sort((a, b) => {
      return new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime();
    });

    const firstTimestamp = sortedEvents[0].timestamp;
    const latestTimestamp = sortedEvents[sortedEvents.length - 1].timestamp;

    const durationMs = new Date(latestTimestamp).getTime() - new Date(firstTimestamp).getTime();
    const durationSeconds = durationMs / 1000;

    // Round to one decimal point
    return Math.round(durationSeconds * 10) / 10;
  };

  const getCurrentCliveThinkingTitle = (events: FirestoreAgentProgressEvent[], shouldShowThinking: boolean): string => {
    if (!shouldShowThinking) {
      const exchangeDuration = getExchangeDuration(events);
      return exchangeDuration ? `Thought for ${exchangeDuration} seconds` : 'Thoughts';
    }

    const lastEvent = events[events.length - 1];
    const title = lastEvent?.title || 'Thinking';

    // Remove existing ... if present, then add ...
    const cleanTitle = title.replace(/\.{3}$/, '');
    return cleanTitle + '...';
  };

  return (
    <div className="__CHAT_PAGE__ bg-gradient-3 flex-1 overflow-auto bg-cover px-20">
      <div className="__CHAT_CONTAINER__ flex h-full w-full justify-center overflow-auto">
        <div className="__CHAT_SCROLABLE__ flex w-full max-w-3xl flex-col gap-20 py-20">
          {isChatPageLoading ? (
            <ChatSkeleton />
          ) : (
            <>
              {exchanges.map((exchange, index) => {
                const isLast = isLastExchange(index);
                const exchangeEvents = organizedUpdates.byExchangeId[exchange.id] || [];
                const hasPendingUpdates = isLast && organizedUpdates.pending.length > 0;
                const shouldShowThinking = isLast && (isChatLoading || hasPendingUpdates);

                const events = shouldShowThinking ? organizedUpdates.pending : exchangeEvents;
                const timelineEntries = convertEventsToTimelineEntries(events);
                const cliveThinkingTitle = getCurrentCliveThinkingTitle(events, shouldShowThinking);

                return (
                  <div key={exchange.id} className="mb-20 flex flex-col items-end gap-12">
                    <ChatMessage
                      message={exchange.user_input}
                      sender="You"
                      className="bg-bluesky-700/5 rounded-lg"
                      createdAt={parseDateWithOptionalTimezone(exchange.created_at)}
                      avatar={<UserAvatar user={user} />}
                      direction="rtl"
                      claimNumber={claimNumber}
                      documentsUsed={exchange.context_document_source_ids}
                    />

                    <CliveChatMessage
                      message={exchange.response}
                      isLoading={isChatLoading && isLast}
                      withTypingEffect={isLast && withTypingEffect}
                      onEndTypingEffect={scrollDown}
                      claimNumber={claimNumber}
                      documentsUsed={exchange.documents_used}
                      messagePrefix={<CliveChatThinking timelineEvents={timelineEntries} title={cliveThinkingTitle} />}
                    />
                  </div>
                );
              })}
            </>
          )}

          <div className="__CHAT_SPACER__ min-h-[200px]" ref={endOfChatRef} />

          <div className="__CHAT_INPUT_CONTAINER__ fixed bottom-20 w-full max-w-3xl">
            {isEmpty(uploadedFiles) ? null : (
              <UploadedFilesPanel
                uploadedFiles={uploadedFiles}
                onRemove={onRemoveUploadedFile}
                className="mx-20 -mb-4 rounded-t-lg p-16 pb-12"
                disabled={isChatLoading}
              />
            )}
            <ChatInput
              value={value}
              rows={1}
              onTextChange={(e) => setValue(e.target.value)}
              onSubmit={sendChatMessage}
              className={cn('__CHAT_INPUT__ w-full')}
              placeholder="Ask me anything..."
              disableSubmit={isChatLoading}
              actions={chatInputActions}
              tags={selectedContextDocuments.map((doc) => ({
                id: doc.id,
                text: doc.document_name || '',
                icon: <DocumentIcon className="h-16 w-16 text-slate-800" />,
              }))}
              onTagClose={(tagId) =>
                setSelectedContextDocuments(selectedContextDocuments.filter((doc) => doc.id !== tagId))
              }
              predefinedPrompts={predefinedPrompts}
              onPromptSelect={handlePromptSelect}
            />
          </div>
        </div>
      </div>
      {isChatContextDialogOpen ? (
        <ChatContextDialog
          isOpen
          onClose={() => setIsChatContextDialogOpen(false)}
          documents={claimDocuments}
          selected={selectedContextDocuments}
          onSubmit={(selectedDocuments) => {
            setSelectedContextDocuments(selectedDocuments);
            setIsChatContextDialogOpen(false);
          }}
        />
      ) : null}
      {openFileUpload ? (
        <FileUploadModal
          isOpen
          uploadUrl={`/api/v1/claims/${claimNumber}/documents/files`}
          onClose={() => setOpenFileUpload(false)}
          onSubmit={onSubmitFiles}
        />
      ) : null}
    </div>
  );
};

export default Chat;
